import React, { useState, useEffect } from 'react';
import { Plus, X, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast, Toaster } from 'react-hot-toast';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import heartLoading from '../../../../../assests/heartLoading.gif';
import preloader from '../../../../../assests/preloader1.gif';

const SLAandAlerts = ({ publishData, onDataRefresh }) => {
  const [receiveAlerts, setReceiveAlerts] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [receiveTeamsAlerts, setReceiveTeamsAlerts] = useState(false);
  const [receiveEmailAlerts, setReceiveEmailAlerts] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [alertToDelete, setAlertToDelete] = useState(null);
  const [emails, setEmails] = useState(['']);
  const [emailErrors, setEmailErrors] = useState(['']);
  const [alertType, setAlertType] = useState("");
  const [alertTeamConfig, setAlertTeamConfig] = useState({
    channel_name: '',
    webhook_link: '',
  });
  const [alertEmailConfig, setAlertEmailConfig] = useState({
    email: '',
    alert_url: process.env.REACT_APP_EMAIL_ALERT_URL
  });

  const [formData, setFormData] = useState({
      product_id: publishData?.ProductInfo?.productId,
      alert_type: '',
      alert_config: '',
      is_active: true,
      created_by: localStorage.getItem('userName') || 'user',
      last_modified_by: localStorage.getItem('userName') || 'user',
    });

    
  useEffect(() => {
    const alertType = receiveTeamsAlerts ? "TEAMS" : "EMAIL";
    const alertConfig = alertType === "TEAMS" ? alertTeamConfig : alertEmailConfig;
    
    setAlertType(alertType);
    setFormData(prev => ({ ...prev, alert_type: alertType, alert_config: JSON.stringify(alertConfig) }));
    console.log("form data", formData);
  }, [receiveEmailAlerts, receiveTeamsAlerts, alertTeamConfig, alertEmailConfig, emails]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@effem\.com$/;

    return emailRegex.test(email);
  };

  const handleEmailChange = (index, value) => {
    const newEmails = [...emails];
    const newErrors = [...emailErrors];
    
    newEmails[index] = value;
    newErrors[index] = value === '' ? '' : (!validateEmail(value) ? 'Only @aaysinsight.com email addresses are allowed' : '');
    
    setEmails(newEmails);
    alertEmailConfig.email = newEmails.join(', ');;
    setEmailErrors(newErrors);
  };

  const addEmail = () => {
    if (emails.length < 5) {
      setEmails([...emails, '']);
      setEmailErrors([...emailErrors, '']);
    }
  };

  const removeEmail = (index) => {
    if (emails.length > 1) {
      const newEmails = emails.filter((_, i) => i !== index);
      const newErrors = emailErrors.filter((_, i) => i !== index);
      setEmails(newEmails);
      setEmailErrors(newErrors);
    }
  };

  const setTeamsMode = () =>{
    setReceiveTeamsAlerts(true);
    setReceiveEmailAlerts(false);
  }

  const setEmailMode = () =>{
    setReceiveTeamsAlerts(false);
    setReceiveEmailAlerts(true);
  }

  const handleInputChange = (field, value) => {
    setAlertTeamConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    setLoading(true);
    
    // Construct the payload directly to avoid state update timing issues
    const alertConfig = receiveTeamsAlerts ? alertTeamConfig : alertEmailConfig;
    const payload = {
      product_id: publishData?.ProductInfo?.productId,
      alert_type: receiveTeamsAlerts ? "TEAMS" : "EMAIL",
      alert_config: JSON.stringify(alertConfig),
      is_active: true,
      created_by: localStorage.getItem('userName') || 'user',
      last_modified_by: localStorage.getItem('userName') || 'user',
    };
    
    axios.post(`${process.env.REACT_APP_API_URL}/alerts/create/${publishData?.ProductInfo?.productId}`, payload)
            .then(res => {
              toast.success('Alert configuration submitted.');
              setReceiveAlerts(false);
              setReceiveEmailAlerts(false);
              setReceiveTeamsAlerts(false);
              setEmails(['']);
              setAlertTeamConfig({
                channel_name: "",
                webhook_link:""
              });
              
              // Refresh publish data to show the new alert
              fetchLatestPublishData();
            })
            .catch(() => {
              toast.error('Failed to submit alert');
              setLoading(false);
            })
            .finally(() => {
              setLoading(false);
            }
    );
  }

  const fetchLatestPublishData = async () => {
    if (!publishData?.ProductInfo?.productId) return;
    
    setFetchLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/publish-overview/${publishData.ProductInfo.productId}`);
      // Update the publishData by calling the parent callback
      if (onDataRefresh) {
        onDataRefresh(response.data);
      }
    } catch (error) {
      console.error('Error fetching latest publish data:', error);
      toast.error('Failed to refresh alert data');
    } finally {
      setFetchLoading(false);
    }
  };

  const handleDeleteClick = (alertId) => {
    setAlertToDelete(alertId);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!alertToDelete) return;

    setDeleteLoading(true);
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/alerts/${alertToDelete}`);
      toast.success('Alert deleted successfully');
      setDeleteConfirmOpen(false);
      setAlertToDelete(null);
      // Refresh the publish data to update the alerts list
      await fetchLatestPublishData();
    } catch (error) {
      toast.error('Failed to delete alert');
      console.error('Error deleting alert:', error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setAlertToDelete(null);
  };

  // Check if Teams or Email alerts already exist
  const hasTeamsAlert = publishData?.SLAandAlerts?.Alerts?.some(alert => alert.AlertType === "Teams");
  const hasEmailAlert = publishData?.SLAandAlerts?.Alerts?.some(alert => alert.AlertType === "Email");

  const isAddButtonDisabled = () => {
    return emails.length >= 5 || emails.some(email => email === '' || !validateEmail(email));
  };

   const isSubmitButtonDisabled = () => {
    if(alertType === "TEAMS")
      return alertTeamConfig.channel_name === '' || alertTeamConfig.webhook_link === '';
    return emails.some(email => email === '' || !validateEmail(email));
  };
  
  const isLoading = loading || deleteLoading || fetchLoading;
  const getLoadingMessage = () => {
    if (loading) return 'Submitting alert...';
    if (deleteLoading) return 'Deleting alert...';
    if (fetchLoading) return 'Refreshing data...';
    return 'Loading...';
  };

  return (
    <div className="space-y-8 relative">
      {/* Full Page Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 flex flex-col items-center space-y-4 shadow-2xl">
            <img src={preloader} alt="Loading..." className="h-32 w-32" />
            <div className="text-center">
              <p className="text-gray-700 text-xl font-semibold">
                {getLoadingMessage()}
              </p>
              <p className="text-gray-500 text-base mt-2">Please wait while we process your request</p>
            </div>
          </div>
        </div>
      )}

      {/* Product SLA Section */}
      <div>
        <h3 className="text-xl font-bold text-gray-800 mb-4">Product SLA</h3>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-[#0000a0]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Label</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Value</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              <tr className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">SLA - Response Time</td>
                <td className="px-6 py-4 text-sm text-gray-700">{publishData?.SLAandAlerts?.slaResponseTime || '99'} hours</td>
              </tr>
              <tr className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">SLA - Uptime</td>
                <td className="px-6 py-4 text-sm text-gray-700">{publishData?.SLAandAlerts?.slaUptime || '99'}%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Existing Alerts Display Section */}
      {publishData?.SLAandAlerts?.Alerts && publishData.SLAandAlerts.Alerts.length > 0 && (
        <div>
          <h3 className="text-xl font-bold text-gray-800 mb-4">Existing Alerts</h3>
          
          {/* Teams Alerts Table */}
          {publishData.SLAandAlerts.Alerts.some(alert => alert.AlertType === "Teams") && (
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-700 mb-3">Teams Alerts</h4>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <table className="min-w-full">
                  <thead className="bg-[#0000a0]">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Channel Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Webhook Link</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {publishData.SLAandAlerts.Alerts
                      .filter(alert => alert.AlertType === "Teams")
                      .map((alert, index) => (
                        <tr key={index} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {alert.ChannelName || 'N/A'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700">
                            <a 
                              href={alert.WebhookLink} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 underline"
                            >
                              {alert.WebhookLink || 'N/A'}
                            </a>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <button
                              onClick={() => handleDeleteClick(alert?.AlertId)}
                              className="inline-flex items-center px-3 py-1 border border-red-300 text-red-700 rounded-md hover:bg-red-50 transition-colors"
                              title="Delete alert"
                              disabled={isLoading}
                            >
                              <Trash2 size={16} className="mr-1" />
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Email Alerts Table */}
          {publishData.SLAandAlerts.Alerts.some(alert => alert.AlertType === "Email") && (
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-700 mb-3">Email Alerts</h4>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <table className="min-w-full">
                  <thead className="bg-[#0000a0]">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Email Addresses</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {publishData.SLAandAlerts.Alerts
                      .filter(alert => alert.AlertType === "Email")
                      .map((alert, index) => (
                        <tr key={index} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 text-sm text-gray-700">
                            <div className="flex flex-wrap gap-2">
                              {alert.Emails && alert.Emails.length > 0 ? (
                                alert.Emails.map((email, emailIndex) => (
                                  <span 
                                    key={emailIndex}
                                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                  >
                                    {email}
                                  </span>
                                ))
                              ) : (
                                <span className="text-gray-500">No emails configured</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <button
                              onClick={() => handleDeleteClick(alert?.AlertId)}
                              className="inline-flex items-center px-3 py-1 border border-red-300 text-red-700 rounded-md hover:bg-red-50 transition-colors"
                              title="Delete alert"
                              disabled={isLoading}
                            >
                              <Trash2 size={16} className="mr-1" />
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Alert Info Section */}
      <div>
        <h3 className="text-xl font-bold text-gray-800 mb-4">Alert Info</h3>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-[#0000a0]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Setting</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Configuration</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              <tr className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Receive DQ Alerts</td>
                <td className="px-6 py-4 text-sm text-gray-700">
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="receiveAlerts"
                        value="yes"
                        checked={receiveAlerts === true}
                        onChange={() => setReceiveAlerts(true)}
                        className="w-4 h-4 text-[#0000A0] border-gray-300 focus:ring-[#0000A0]"
                        disabled={isLoading}
                      />
                      <span className="text-gray-700">Yes</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="receiveAlerts"
                        value="no"
                        checked={receiveAlerts === false}
                        onChange={() => setReceiveAlerts(false)}
                        className="w-4 h-4 text-[#0000A0] border-gray-300 focus:ring-[#0000A0]"
                        disabled={isLoading}
                      />
                      <span className="text-gray-700">No</span>
                    </label>
                  </div>
                </td>
              </tr>
              {receiveAlerts && (
              <tr className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Alert Type</td>
                <td className="px-6 py-4 text-sm text-gray-700">
                  <div className="flex items-center space-x-4">
                    <Tooltip 
                      title={hasTeamsAlert ? "You already have a Teams alert. If you want to add new, then you need to remove the previous Teams alert" : ""}
                      arrow
                    >
                      <label className={`flex items-center space-x-2 ${hasTeamsAlert ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                        <input
                          type="radio"
                          name="alertMode"
                          value="TEAMS"
                          checked={receiveTeamsAlerts === true}
                          onChange={() => setTeamsMode()}
                          disabled={hasTeamsAlert || isLoading}
                          className="w-4 h-4 text-[#0000A0] border-gray-300 focus:ring-[#0000A0]"
                        />
                        <span className="text-gray-700">Teams</span>
                      </label>
                    </Tooltip>
                    <Tooltip 
                      title={hasEmailAlert ? "You already have an Email alert. If you want to add new, then you need to remove the previous Email alert" : ""}
                      arrow
                    >
                      <label className={`flex items-center space-x-2 ${hasEmailAlert ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                        <input
                          type="radio"
                          name="alertMode"
                          value="EMAIL"
                          checked={receiveEmailAlerts === true}
                          onChange={() => setEmailMode()}
                          disabled={hasEmailAlert || isLoading}
                          className="w-4 h-4 text-[#0000A0] border-gray-300 focus:ring-[#0000A0]"
                        />
                        <span className="text-gray-700">Email</span>
                      </label>
                    </Tooltip>
                  </div>
                </td>
              </tr>
            )}
              {receiveEmailAlerts && receiveAlerts && (
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Email Addresses</td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    <div className="space-y-3">
                      <div className="text-sm text-gray-600 mb-2">
                        Enter email address (You can add up to 5 @aaysinsight.com email addresses):
                      </div>
                      
                      {/* Email Input Fields */}
                      <div className="space-y-2">
                        {emails.map((email, index) => (
                          <div key={index} className="flex items-center space-x-2">
                            <div className="flex-1">
                              <input
                                type="email"
                                value={email}
                                onChange={(e) => handleEmailChange(index, e.target.value)}
                                placeholder="Enter @aaysinsight.com email address"
                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${
                                  emailErrors[index] ? 'border-red-500' : 'border-gray-300'
                                }`}
                                disabled={isLoading}
                              />
                              {emailErrors[index] && (
                                <p className="text-red-500 text-xs mt-1">{emailErrors[index]}</p>
                              )}
                            </div>
                            {emails.length > 1 && (
                              <button
                                onClick={() => removeEmail(index)}
                                className="p-1 text-red-600 hover:text-red-800 transition-colors"
                                title="Remove email"
                                disabled={isLoading}
                              >
                                <X size={14} />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Add Another Email Button */}
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={addEmail}
                          disabled={isAddButtonDisabled() || isLoading}
                          className={`flex items-center space-x-2 px-3 py-1 rounded text-sm font-medium transition-colors ${
                            isAddButtonDisabled() || isLoading
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              : 'bg-[#0000A0] text-white hover:bg-[#0000A0]'
                          }`}
                        >
                          <Plus size={14} />
                          <span>Add another email</span>
                        </button>
                        {emails.length >= 5 && (
                          <span className="text-xs text-gray-500">Maximum 5 email addresses reached</span>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              )}
              {receiveTeamsAlerts && receiveAlerts && (
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Teams Configuration</td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    <div className="space-y-3">
                      
                      <div className="text-sm text-gray-600 mb-2">Channel Name</div>
                      <div className="space-y-2">
                        <input
                        type="text"
                        value={alertTeamConfig.channel_name}
                        onChange={e => handleInputChange('channel_name', e.target.value)}
                        placeholder="Enter Channel name"
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        disabled={isLoading}
                      />
                      </div>

                      <div className="text-sm text-gray-600 mb-2">Webhook Link</div>
                      <div className="space-y-2">
                        <input
                        type="text"
                        value={alertTeamConfig.webhook_link}
                        onChange={e => handleInputChange('webhook_link', e.target.value)}
                        placeholder="Enter webhook link"
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        disabled={isLoading}
                      />
                      </div>
                    </div>
                  </td>
                </tr>          
              )}
              <tr className="hover:bg-gray-50 transition-colors items-center">
                <td className="px-6 py-4 text-sm text-gray-700">
                <button 
                  disabled={isSubmitButtonDisabled() || isLoading}
                  className={`flex items-center space-x-2 px-3 py-1 rounded text-sm font-medium transition-colors ${
                    isSubmitButtonDisabled() || isLoading
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-[#0000A0] text-white hover:bg-[#0000A0]'
                  }`}
                  onClick={() => handleSubmit()}
                >
                  Submit Alert
              </button>
              </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1.25rem', color: '#1f2937' }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <p className="text-gray-700 text-base">
            Are you sure you want to delete this alert? This action cannot be undone.
          </p>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button
            onClick={handleDeleteCancel}
            variant="outlined"
            disabled={isLoading}
            sx={{
              color: '#6b7280',
              borderColor: '#d1d5db',
              '&:hover': {
                borderColor: '#9ca3af',
                backgroundColor: '#f9fafb'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            disabled={isLoading}
            sx={{
              backgroundColor: '#dc2626',
              '&:hover': {
                backgroundColor: '#b91c1c'
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export { SLAandAlerts }; 